import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import ConfettiAnimation from './ConfettiAnimation';

const UpgradeCelebrationModal = ({ 
  isOpen = false, 
  onClose = () => {},
  planName = 'Pro'
}) => {
  const [showConfetti, setShowConfetti] = useState(false);
  const [animationStep, setAnimationStep] = useState(0); // 0: hidden, 1: fade-in, 2: visible

  useEffect(() => {
    if (isOpen) {
      setShowConfetti(true);
      // Delay the modal animation slightly so confetti starts first
      setTimeout(() => {
        setAnimationStep(1);
        // Then make it fully visible
        setTimeout(() => {
          setAnimationStep(2);
        }, 100);
      }, 200);
    } else {
      setShowConfetti(false);
      setAnimationStep(0);
    }
  }, [isOpen]);

  const handleConfettiComplete = () => {
    setShowConfetti(false);
  };

  const handleClose = () => {
    setShowConfetti(false);
    setAnimationStep(0);
    onClose();
  };

  return (
    <>
      {/* Confetti Animation */}
      <ConfettiAnimation
        isActive={showConfetti}
        duration={4000}
        particleCount={150}
        onComplete={handleConfettiComplete}
      />
      
      {/* Celebration Modal */}
      <Modal
        show={isOpen}
        onHide={handleClose}
        centered
        backdrop="static"
        keyboard={false}
        className="upgrade-celebration-modal"
        style={{
          zIndex: 10000 // Above confetti
        }}
      >
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '24px',
            padding: '32px 24px 28px 24px',
            textAlign: 'center',
            color: '#1d1d1f',
            border: '1px solid rgba(255, 255, 255, 0.8)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 32px rgba(0, 0, 0, 0.08)',
            transform: animationStep === 1 ? 'scale(0.8)' : animationStep === 2 ? 'scale(1)' : 'scale(0.7)',
            opacity: animationStep === 0 ? 0 : 1,
            transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
            position: 'relative',
            width: '100%'
          }}
        >
          <Modal.Body style={{ padding: 0, border: 'none' }}>
            {/* Close button */}
            <button
              onClick={handleClose}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: '#f5f5f7',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontSize: '14px',
                color: '#8e8e93'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#e5e5ea';
                e.target.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#f5f5f7';
                e.target.style.transform = 'scale(1)';
              }}
            >
              ✕
            </button>

            {/* Success Icon */}
            <div
              style={{
                width: '80px',
                height: '80px',
                background: '#f0f9ff',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem auto',
                fontSize: '2.5rem',
                animation: animationStep === 2 ? 'bounce 1s ease-out' : 'none',
                border: '2px solid #0ea5e9'
              }}
            >
              🎉
            </div>

            {/* Celebration Message */}
            <h2
              style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#1d1d1f',
                marginBottom: '8px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif',
                lineHeight: '1.2'
              }}
            >
              Congratulations!
            </h2>

            <p
              style={{
                fontSize: '16px',
                fontWeight: '500',
                color: '#86868b',
                marginBottom: '20px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif'
              }}
            >
              Your account has been upgraded to a <strong>{planName} membership</strong>!
            </p>

            <p
              style={{
                fontSize: '15px',
                fontWeight: '400',
                color: '#515154',
                lineHeight: '1.5',
                marginBottom: '32px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif'
              }}
            >
              Enjoy unlimited links, advanced analytics, and priority support.
            </p>

            {/* Features List */}
            <div
              style={{
                backgroundColor: '#f5f5f7',
                borderRadius: '16px',
                padding: '18px 20px',
                marginBottom: '24px'
              }}
            >
              <h3
                style={{
                  fontSize: '17px',
                  fontWeight: '600',
                  color: '#1d1d1f',
                  marginBottom: '10px',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif'
                }}
              >
                ✨ What you get with Pro:
              </h3>
              
              {[
                { icon: '🚀', text: 'Unlimited links' },
                { icon: '📊', text: 'Additional data analytics' },
                { icon: '⚡', text: 'Priority support' }
              ].map((feature, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: index === 2 ? '0' : '6px',
                    fontSize: '15px',
                    color: '#515154',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif'
                  }}
                >
                  <span style={{ marginRight: '12px', fontSize: '16px' }}>{feature.icon}</span>
                  {feature.text}
                </div>
              ))}
            </div>

            {/* Close Button */}
            <button
              onClick={handleClose}
              style={{
                width: '100%',
                padding: '16px 24px',
                backgroundColor: '#007AFF',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '17px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
                boxShadow: '0 4px 12px rgba(0, 122, 255, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#0056b3';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 6px 16px rgba(0, 122, 255, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#007AFF';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 122, 255, 0.3)';
              }}
            >
              Get Started
            </button>
          </Modal.Body>
        </div>
      </Modal>

      <style jsx>{`
        @keyframes bounce {
          0%, 20%, 53%, 80%, 100% {
            transform: translate3d(0, 0, 0);
          }
          40%, 43% {
            transform: translate3d(0, -30px, 0);
          }
          70% {
            transform: translate3d(0, -15px, 0);
          }
          90% {
            transform: translate3d(0, -4px, 0);
          }
        }
        
        .upgrade-celebration-modal .modal-content {
          background: transparent !important;
          border: none !important;
        }
        
        .upgrade-celebration-modal .modal-dialog {
          max-width: 480px;
        }
      `}</style>
    </>
  );
};

export default UpgradeCelebrationModal;